import { createSlice } from "@reduxjs/toolkit";

/**
 * Marks storeIds that already have widgets as visited (SSR / seeded content).
 * @param {Record<string, boolean>} visitedStoreIds
 * @param {Array<{ storeId?: string, feed?: { widgets?: Array } }>} stores
 */
function markStoresWithFeedAsVisited(visitedStoreIds, stores = []) {
  stores.forEach((store) => {
    if (store?.storeId && (store?.feed?.widgets?.length ?? 0) > 0) {
      visitedStoreIds[store.storeId] = true;
    }
  });
}

const initialState = {
  pageStores: [],
  desktopWidgets: [],
  isLoading: false,
  error: null,
  /**
   * UI-only first-visit map. Controls HomeSkeleton — not whether the API runs.
   * Once a store has been shown, later switches still refetch but skip the shimmer.
   */
  visitedStoreIds: {},
};

export const DynamicHomeSlice = createSlice({
  name: "dynamicHome",
  initialState,
  reducers: {
    /**
     * Marks the tapped store as selected and deselects all others.
     * Shimmer (`isLoading`) only on that store's first visit when it has no widgets yet.
     * Does not change `forceCall` / API behavior — the saga still handles that.
     *
     * This action is also the saga trigger — the saga watches it,
     * fetches the feed if needed, and writes the result back via setSelectedStoreFeed.
     *
     * @param {{ storeId: string, storeName?: string, stores?: Array, forceCall?: boolean, pincode?: string, email?: string, fnpId?: string }} action.payload
     *   forceCall — when true, saga bypasses the cache and re-fetches even if feed exists.
     *   storeName — masthead API brand selector. storeId is only used for Redux slot matching.
     */
    requestMobileStoreFeed: (state, action) => {
      const { storeId, stores } = action.payload;

      // Lazy init — seed pageStores from SSR data on the very first tab click.
      // No-op on subsequent clicks (length guard).
      if (state.pageStores.length === 0 && stores?.length > 0) {
        state.pageStores = stores.map((store) => ({ ...store }));
        markStoresWithFeedAsVisited(state.visitedStoreIds, stores);
      }

      state.pageStores = state.pageStores.map((store) => ({
        ...store,
        isSelected: store.storeId === storeId,
      }));

      const targetStore = state.pageStores.find((s) => s.storeId === storeId);
      const hasCachedWidgets = (targetStore?.feed?.widgets?.length ?? 0) > 0;
      const hasBeenVisited = Boolean(state.visitedStoreIds[storeId]);

      // UI only: skip shimmer if this store was already visited or already has content.
      // forceCall still reaches the saga unchanged for API refetch.
      if (hasBeenVisited || hasCachedWidgets) {
        if (hasCachedWidgets) {
          state.visitedStoreIds[storeId] = true;
        }
        state.isLoading = false;
        return;
      }

      // First visit and no content yet → show shimmer until feed arrives.
      state.isLoading = true;
      state.error = null;
    },

    /**
     * Writes a fetched feed into the correct store slot and marks the store visited
     * so later switches skip the shimmer (API may still run again).
     * @param {{ storeId: string, response: object }} action.payload
     */
    setSelectedStoreFeed: (state, action) => {
      const { storeId, response } = action.payload;

      if (state.pageStores.length === 0 && response?.stores?.length > 0) {
        state.pageStores = response.stores.map((store) => ({ ...store }));
        markStoresWithFeedAsVisited(state.visitedStoreIds, response.stores);
      } else {
        const responseStore = response?.stores?.find((s) => s.storeId === storeId);
        const store = state.pageStores.find((s) => s.storeId === storeId);
        if (store && responseStore) {
          store.feed = responseStore.feed ?? null;
          store.curatedWidgets = responseStore.curatedWidgets ?? null;
        }
      }

      if (storeId) {
        state.visitedStoreIds[storeId] = true;
      }
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Saga trigger for re-fetching the desktop home feed.
     * @param {{ pincode: string, email: string, fnpId: string }} action.payload
     */
    requestDesktopFeed: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    /**
     * Writes freshly fetched desktop widgets into the store.
     * @param {Array} action.payload - Widget objects from the API response.
     */
    setDesktopFeed: (state, action) => {
      state.desktopWidgets = action.payload ?? [];
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Merges campaign-personalized widgets into a mobile store feed.
     * @param {{ storeId: string, widgets: Array, stores?: Array }} action.payload
     */
    setMobileStoreCampaignFeed: (state, action) => {
      const { storeId, widgets, stores } = action.payload;
      if (state.pageStores.length === 0 && stores?.length > 0) {
        state.pageStores = stores.map((store) => ({ ...store }));
        markStoresWithFeedAsVisited(state.visitedStoreIds, stores);
      }
      const store = state.pageStores.find((item) => item.storeId === storeId);
      if (store) {
        store.feed = {
          ...(store.feed ?? {}),
          widgets: widgets ?? [],
        };
      }
      if (storeId && (widgets?.length ?? 0) > 0) {
        state.visitedStoreIds[storeId] = true;
      }
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Marks a failed async feed fetch.
     * @param {string} action.payload - Human-readable error message.
     */
    fetchFeedFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  requestMobileStoreFeed,
  setSelectedStoreFeed,
  requestDesktopFeed,
  setDesktopFeed,
  setMobileStoreCampaignFeed,
  fetchFeedFailure,
} = DynamicHomeSlice.actions;

export default DynamicHomeSlice.reducer;
